<template>
    <div>
        <products-added-to-cart v-show="productsAddedToCart" class="mt-sm md:mt-3xl" />
        <transition name="cart">
            <div v-show="! productsAddedToCart" class="card has-footer mt-sm md:mt-3xl">
                <h1 class="font-regular">{{ product.name }}</h1>

                <div v-if="campaigns.length > 0" class="md:flex max-w-xl md:mb-5xl">
                    <variant
                        v-for="campaign in campaigns"
                        :key="campaign.id"
                        :variant="campaign"
                        :is-campaign="true"
                        :selected-variant="selectedCampaign"
                        :product-type="lang('Kampanj','Campaign')"
                        :product-variation="campaign.name"
                        :price="getCampaignPrice(campaign)"
                        :price-after-campaign="getCampaignAfterPrice(campaign)"
                        @select="setCampaign"
                    />

                    <variant
                        :is-campaign="false"
                        :variant="product"
                        :selected-variant="selectedProduct"
                        :product-variation="product.name"
                        :price="format_sek_per_month(product.monthly_price)"
                        @select="setProduct"
                    />
                </div>

                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="selectedProduct && product.description"
                    class="text-md text-neutral-dark mb-lg md:mb-5xl lg:max-w-lg"
                    v-html="product.description"
                />
                <div
                    v-else-if="selectedCampaign && selectedCampaign.description"
                    class="text-md text-neutral-dark mb-lg md:mb-5xl lg:max-w-lg"
                    v-html="selectedCampaign.description"
                />

                <setting-with-text
                    :setting="bindingPeriodLabel"
                    :title="lang('Bindningstid','Binding period')"
                    :description="lang('Tjänstens bindningstid', 'The service binding period')"
                />

                <setting-with-text
                    :setting="terminationTimeLabel"
                    :title="lang('Uppsägningstid','Termination time')"
                    :description="lang('Tjänstens uppsägningstid','The service termination time')"
                />

                <setting-with-text
                    v-if="startFee > 0"
                    :setting="startFeeLabel"
                    :title="lang('Startavgift','Initial cost')"
                    :description="lang('Tjänstens startavgift','The service initial cost')"
                />

                <setting-with-radio
                    v-model="numberSettingType"
                    :title="lang('Nummerhantering','Number settings')"
                    description=""
                    :options="selectablePhoneNumberSettings"
                />

                <move-number-setting
                    v-if="numberSettingType === 'move'"
                    :description="lang('Startdatumet för ditt nya telefonnummer kan först vara 3 veckor framåt','The start date for your new phone number must be at least three weeks from now')"
                    :delivery-min-date="deliveryPossibleFromDates.move"
                    :delivery-max-date="deliveryPossibleUntilDates.move"
                    @change="updateMoveNumber"
                />

                <new-number-setting
                    v-if="numberSettingType === 'new'"
                    :area-codes="availableAreaCodes"
                    :description="lang('Startdatumet för ditt porterade telefonnummer kan först vara 5 dagar framåt','The start date must be at least five days from now')"
                    :delivery-min-date="deliveryPossibleFromDates.new"
                    :delivery-max-date="deliveryPossibleUntilDates.new"
                    @change="updateNewNumber"
                />

                <setting-with-checkbox
                    v-model="hiddenNumber"
                    :options="[
                        {
                            name: 'hiddenNumber',
                            value: 1,
                            valuePrice: lang('Ingen kostnad','No cost'),
                            label: lang('Hemligt nummer','Hidden number'),
                        }
                    ]"
                    :title="lang('Tillval','Add-on')"
                    :description="translation('services_telephony_hidden_number_expl')"
                />


                <setting-with-checkbox
                    v-model="numberReporting"
                    :options="[
                        {
                            name: 'numberReporting',
                            value: 1,
                            valuePrice: '',
                            label: lang('Rapportera till nummerupplysningen','Inform number services about your phone number'),
                        }
                    ]"
                    title="Tillval"
                    :disabled-explanation="hiddenNumber ? lang('Kan inte redigeras när hemligt nummer är valt','Cannot edit when hidden number is selected') : null"
                    :disabled="!! hiddenNumber"
                    :description="translation('services_telephony_number_reporting_expl')"
                />


                <setting-with-checkbox
                    v-if="configurationData.displaySendRouterImmediately"
                    v-model="sendRouterImmediately"
                    :options="[
                        {
                            name: 'sendRouterImmediately',
                            value: 1,
                            valuePrice: '',
                            label: lang('Skicka router snarast', 'Send the router immediately'),
                        }
                    ]"
                    :title="lang('Tillval','Add-on')"
                    :description="lang(
                        'Normalt skickas din tjänstefördelare/router ut några dagar innan din tjänst aktiveras, men du kan här välja att få den skickad direkt',
                        'Normally your router is sent to you a few days before your service is activated, but here you can choose to get it straight away'
                    )"
                />

                <router-selector
                    v-model="selectedRouter"
                    :configuration-data="configurationData"
                    :routers="selectableRouters"
                    :allow-none-selection="! mustSelectRouter"
                />

                <configuration-footer
                    :validates="validates"
                    :monthly-price="monthlyPrice"
                    :one-time-cost="oneTimeCost"
                    @add="addToCart"
                />
            </div>
        </transition>

        <slot v-if="productsAddedToCart" />
    </div>
</template>
<script>
    import SettingWithText from './Settings/SettingWithText';
    import SettingWithRadio from './Settings/SettingWithRadio';
    import SettingWithCheckbox from './Settings/SettingWithCheckbox';
    import MoveNumberSetting from './Settings/MoveNumberSetting';
    import NewNumberSetting from './Settings/NewNumberSetting';
    import Variant from './Variant';
    import ConfigurationFooter from './ConfigurationFooter';
    import BaseConfigurator from './BaseConfigurator';
    import ProductsAddedToCart from './ProductsAddedToCart';
    import RouterSelector from './Hardware/RouterSelector.vue';

    export default {
        components: {
            RouterSelector,
            SettingWithCheckbox,
            SettingWithText,
            SettingWithRadio,
            MoveNumberSetting,
            NewNumberSetting,
            Variant,
            ConfigurationFooter,
            ProductsAddedToCart,
        },
        extends: BaseConfigurator,
        props: {
            configurationData: {
                required: true,
                type: Object,
            },
        },
        data() {
            return {
                product: this.configurationData.product,
                routers: this.configurationData.routers,
                campaigns: this.configurationData.campaigns,
                hasCollectiveRouter: this.configurationData.hasCollectiveRouter,
                selectedProduct: this.configurationData.product,
                selectedCampaign: null,
                deliveryPossibleFromDates: this.configurationData.deliveryPossibleFromDates,
                deliveryPossibleUntilDates: this.configurationData.deliveryPossibleUntilDates,
                availableAreaCodes: this.configurationData.availableAreaCodes,
                hiddenNumber: false,
                numberReporting: false,
                sendRouterImmediately: false,
                numberSettingType: null,
                moveNumberData: {
                    areaCode: '',
                    phoneNumber: '',
                    startDate: '',
                },
                newNumberData: {
                    areaCode: '',
                    phoneNumber: '',
                    startDate: '',
                },
                selectedRouter: null,
            };
        },
        computed: {
            numberSettingData() {
                const data = this.numberSettingType === 'move' ? this.moveNumberData : this.newNumberData;
                data.hiddenNumber = this.hiddenNumber;
                data.numberReporting = this.numberReporting;
                data.sendRouterImmediately = this.sendRouterImmediately;
                data.type = this.numberSettingType;

                return data;
            },
            validates() {
                return !! (this.numberSettingData.areaCode && this.numberSettingData.phoneNumber && this.numberSettingData.startDate);
            },
            selectablePhoneNumberSettings() {
                const phoneNumberSettings = [];

                phoneNumberSettings.push({
                    value: 'new',
                    name: 'number',
                    placeholder: lang('Nytt nummer','New number'),
                });

                let portingFee = null;

                if (this.portingFee > 0) {
                    portingFee = format_sek(this.portingFee);
                }

                phoneNumberSettings.push({
                    value: 'move',
                    name: 'number',
                    placeholder: lang('Flytta befintligt nummer','Transfer existing phone number'),
                    price_label: portingFee,
                });

                return phoneNumberSettings;
            },
            portingFee() {
                return this.getAttributeFromSelected('porting_fee');
            },
            oneTimeCost() {
                let oneTimeCost = this.startFee;

                if (this.selectedRouter) {
                    oneTimeCost += this.selectedRouter.price;
                }

                if (this.numberSettingType === 'move') {
                    oneTimeCost += this.portingFee;
                }

                return format_sek(oneTimeCost);
            },
            selectableRouters() {
                return this.selectedCampaignHasRouter ? this.selectedCampaign.routers : this.routers;
            },
            mustSelectRouter() {
                if (this.selectedCampaignHasRouter) {
                    return true;
                }

                return this.selectedProduct && this.selectedProduct.requires_router;
            },
        },
        watch: {
            hiddenNumber(value) {
                if (value) {
                    this.numberReporting = false;
                }
            },
        },
        created() {
            this.$GtmTracker.productDetailView([this.product.gtm_product_data]);
        },
        methods: {
            updateMoveNumber(areaCode, phoneNumber, startDate) {
                this.moveNumberData.areaCode = areaCode;
                this.moveNumberData.phoneNumber = phoneNumber;
                this.moveNumberData.startDate = startDate;
            },
            updateNewNumber(areaCode, phoneNumber, startDate) {
                this.newNumberData.areaCode = areaCode;
                this.newNumberData.phoneNumber = phoneNumber;
                this.newNumberData.startDate = startDate;
            },
            setCampaign(newCampaign) {
                this.selectedCampaign = newCampaign;
                this.selectedProduct = null;
            },
            async addToCart() {
                const articles = [];

                const telephonyProduct = {
                    articleClass: this.getAttributeFromSelected('article_class'),
                    articleId: this.getAttributeFromSelected('article_id'),
                    subItems: {},
                    parameters: {
                        numberSettingData: this.numberSettingData,
                    },
                };

                if (this.selectedCampaignHasRouter && this.selectedRouter) {
                    telephonyProduct['subItems']['router'] = {
                        articleClass: this.selectedRouter.article_class,
                        articleId: this.selectedRouter.article_id,
                    };
                } else if (this.selectedRouter) {
                    articles.push({
                        articleClass: this.selectedRouter.article_class,
                        articleId: this.selectedRouter.article_id,
                    });
                }

                articles.push(telephonyProduct);

                const response = await axios.post(route('front.api.cart-items.store'), {
                    articles,
                });

                EventBus.$emit('cartDataUpdated', response.data);
                this.productsAddedToCart = true;
            },
        },
    };
</script>
